import {PolymerElement, html} from '@polymer/polymer';
import '@polymer/polymer/lib/elements/dom-if.js';
import {ScopeBinding} from '../behaviors/keli-binding-behavior.js';
import {InputBehavior} from '../behaviors/input-behavior.js';
import {InputSharedStylesHTML} from '../shared-styles/input.js';
import {HotkeyBehavior} from '../behaviors/hotkey-behavior.js';
import {format, subMinutes, addMinutes, getHours, subHours, addHours, isValid} from 'date-fns';
import {parseTime} from '@beauwest/business-date-parser';

class KeliTime extends ScopeBinding(HotkeyBehavior(InputBehavior(PolymerElement))) {
  static get template() {
    return html`
${InputSharedStylesHTML}

<keli-input-container id="container"
    label="[[caption]]"
    input-has-content="[[active]]"
    icon="[[icon]]"
    always-float-label="[[masked]]"
    focused="[[focused]]"
    disabled="[[disabled]]"
    invalid="[[invalid]]"
    error-message="[[errorMessage]]"
    hotkey="[[formattedShortcutKey]]"
    chrome="[[chrome]]"
    help-text="[[helpText]]">

  <input type="text" id="input" value="[[currentValue]]" slot="input">
  <div slot="postscript">[[willParseTo]]</div>
</keli-input-container>`;
  }

  static get is() {
    return 'keli-time';
  }

  static get properties() {
    return {
      // @formatter:off
      computeWireFormatInitialization: Boolean,
      dataFormat: {type: String, value: 'HH:mm:ss'},
      dateTime: {type: Object, computed: 'computeDateTime(currentValue)'},
      keliControlTypes: {type: Array, value: ['time']},
      military: {type: Boolean, scopevar: true},
      seconds: {type: Boolean, scopevar: true},
      willParseTo: String,
      wireFormat: {type: String, computed: 'computeWireFormat(seconds, military, computeWireFormatInitialization)', observer: 'wireFormatChanged'}
      // @formatter:on
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.computeWireFormatInitialization = true;

    this.registerHotkey(KeliInterface.Hotkeys.time.current, {
      down: () => {
        this.setFromHotkey(this.toWireFormat(new Date()));
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.time.subtract, {
      down: () => {
        if (this.dateTime) {
          this.setFromHotkey(this.toWireFormat(subMinutes(this.dateTime, 1)));
        }
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.time.add, {
      down: () => {
        if (this.dateTime) {
          this.setFromHotkey(this.toWireFormat(addMinutes(this.dateTime, 1)));
        }
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.time.am, {
      down: () => {
        let parsed = parseTime(`${this.$.input.value} AM`);
        if (parsed) {
          let hours = getHours(parsed);
          if (hours >= 12) {
            this.setFromHotkey(this.toWireFormat(subHours(parsed, 12)));
          } else {
            this.setFromHotkey(this.toWireFormat(parsed));
          }
        }
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.time.pm, {
      down: () => {
        let parsed = parseTime(`${this.$.input.value} PM`);
        if (parsed) {
          let hours = getHours(parsed);
          if (hours < 12) {
            this.setFromHotkey(this.toWireFormat(addHours(parsed, 12)));
          } else {
            this.setFromHotkey(this.toWireFormat(parsed));
          }
        }
      }
    });
  }

  computeWireFormat(seconds, military) {
    let parts = [
      (military ? 'HH' : 'h'),
      'mm'
    ];
    if (seconds) {
      parts.push('ss');
    }
    let suffix = (military ? '' : ' a');
    return `${parts.join(':')}${suffix}`;
  }

  wireFormatChanged() {
    this.$.input.value = this.toWireFormat(this.$.input.value);
  }

  computeDateTime(currentValue) {
    if (!currentValue) {
      return undefined;
    }
    let parsed = parseTime(currentValue);
    return (isValid(parsed) ? parsed : undefined);
  }

  setFromHotkey(newValue) {
    if (newValue === this.currentValue) {
      this.currentValueChanged(newValue);
    } else {
      this.currentValue = newValue;
    }
    this.fireKeliAccepted = true;
    this.willParseTo = null;
    this._handleInput();
  }

  toWireFormat(newValue, wireFormat) {
    if (!newValue) {
      return null;
    }
    let parsed = parseTime(newValue);

    // Manually compute the wire format if it's not set up yet.
    if (!wireFormat && !this.wireFormat) {
      wireFormat = this.computeWireFormat(this.seconds, this.military);
    }

    return (isValid(parsed) ? format(parsed, wireFormat || this.wireFormat) : undefined);
  }

  displayValueChanged(newValue) {
    if (newValue) {
      return this.toWireFormat(newValue);
    }
  }

  toRawFieldValue(value) {
    return this.toWireFormat(value, this.dataFormat);
  }

  currentValueChanged(newValue) {
    if (this.toRawFieldValue(this.displayValue) !== this.toRawFieldValue(newValue)) {
      this.$.input.value = (newValue ? this.toWireFormat(newValue) : '');
    }
    this.active = !!this.$.input.value;
  }

  handleInput() {
    this.active = !!this.$.input.value;
    let willParseToValue = this.toWireFormat(this.$.input.value);
    this.willParseTo = (willParseToValue && this.$.input.value !== this.currentValue ? `Will parse to: ${willParseToValue}` : undefined);
    if (this.$.input.value === '') {
      this.currentValue = this.$.input.value;
    }
  }

  handleFocus() {
    this.resetHotkeyState();
  }

  keliAccept() {
    const parsed = parseTime(this.$.input.value);
    if (parsed && isValid(parsed)) {
      this.currentValue = this.toWireFormat(parsed);
    }
    if (this.currentValue && this.currentValue !== this.$.input.value) {
      this.$.input.value = this.currentValue;
    }
    this.active = !!this.currentValue;
    this.willParseTo = '';
  }
}

customElements.define(KeliTime.is, KeliTime);
export {KeliTime};
